<template>
  <!--
    CHAT-UX-01 / CX-02 (doc 29 §5.2) -- the text that was read from a
    student's photo, kept on screen after the student answered the tutor's
    «درست است؟». While that question is pending the reading lives in ChatAsk,
    with its controls; once it is answered ChatAsk is gone and this read-only
    record stands in the same place, under the tutor's question. It has no
    control and sends nothing.

    The text is the persisted ask's own `draft` -- the same string ChatAsk
    showed, rendered the same way. The status says how the student answered,
    and only when that is known: «تأیید شد» / «ویرایش شد», otherwise nothing.
  -->
  <section
    class="ai-tutor-reading"
    data-testid="ai-tutor-reading-record"
    :aria-label="title"
  >
    <div class="ai-tutor-reading__head">
      <student-home-icon name="camera" :size="18" />
      <strong>{{ title }}</strong>
      <span
        v-if="statusLabel"
        class="ai-tutor-reading__status"
        :class="`ai-tutor-reading__status--${status}`"
        data-testid="ai-tutor-reading-status"
      >
        <student-home-icon
          v-if="status === 'confirmed'"
          name="check"
          :size="12"
        />
        {{ statusLabel }}
      </span>
    </div>
    <div class="ai-tutor-reading__text" data-testid="ai-tutor-reading-text">
      <markdown-content :source="draft" />
    </div>
  </section>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import MarkdownContent from './MarkdownContent.vue'

const STATUS_KEYS = {
  confirmed: 'aiTutorSetup.chat.ask.readingConfirmed',
  edited: 'aiTutorSetup.chat.ask.readingEdited',
}

export default {
  name: 'ChatReadingRecord',
  components: { StudentHomeIcon, MarkdownContent },
  props: {
    // The persisted ask's `draft`: what was read from the photo.
    draft: {
      type: String,
      required: true,
    },
    // How the student answered, when it is known.
    status: {
      type: String,
      default: '',
      validator: value => ['', 'confirmed', 'edited'].includes(value),
    },
  },
  computed: {
    title() {
      return this.$t('aiTutorSetup.chat.ask.confirmTitle')
    },
    statusLabel() {
      const key = STATUS_KEYS[this.status]
      return key ? this.$t(key) : ''
    },
  },
}
</script>

<style lang="scss" scoped>
// The frame and the reading box are ChatAsk's own confirm card (V2 card
// family, `.related-paths`), so the record stands exactly where the card
// stood; only the actions are gone and a status chip sits in the head.
$v2-card-inset: 43px;

.ai-tutor-reading {
  margin: 0 $v2-card-inset;
  padding: 14px 16px;
  border: 1px solid $borna-indigo-100;
  border-radius: 19px;
  background: linear-gradient(145deg, #f8faff, #fff);
  line-height: 1.75;
}

.ai-tutor-reading__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  > ::v-deep svg {
    flex: none;
    color: $borna-indigo-600;
  }

  > strong {
    color: $borna-text;
    font-size: 11px;
  }
}

.ai-tutor-reading__status {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-inline-start: auto;
  padding: 1px 9px;
  border: 1px solid $borna-border;
  border-radius: 999px;
  background: #fff;
  color: $borna-muted;
  font-size: 9px;
  font-weight: 800;
  line-height: 1.9;
  white-space: nowrap;

  &--confirmed {
    border-color: $borna-teal-50;
    background: $borna-teal-50;
    color: $borna-teal-700;
  }
}

.ai-tutor-reading__text {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid $borna-border;
  border-radius: 13px;
  background: $borna-canvas;
  color: $borna-text;
  font-size: 11px;

  // UX2-04 (doc 30 §4): same pin as ChatAsk's draft -- 14.5px, measured.
  ::v-deep .ai-md .katex {
    font-size: 14.5px;
  }
}

// V2 `@media (width<=620px)`: full width, like the card it replaces.
@media (max-width: 620px) {
  .ai-tutor-reading {
    margin-inline: 0;
  }
}
</style>
